import { useState } from 'react';

interface Ensemble {
  id: string;
  name: string;
  description: string;
  members: number;
  image: string;
  details: string;
  videoUrl: string;
}

interface EnsembleCardProps {
  ensemble: Ensemble;
  index: number;
  isVisible: boolean;
}

export default function EnsembleCard({ ensemble, index, isVisible }: EnsembleCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const handleDoubleClick = () => {
    window.open(ensemble.videoUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`relative overflow-hidden rounded-lg shadow-lg transition-all duration-1000 cursor-pointer ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={handleDoubleClick}
      title="Double-click to watch video"
    >
      {/* Image */}
      <div className="relative h-64 sm:h-80 md:h-96 w-full overflow-hidden">
        <img
          src={ensemble.image}
          alt={ensemble.name}
          className={`w-full h-full object-cover object-top transition-transform duration-700 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
        />
        <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-500 ${
          isHovered ? 'opacity-100' : 'opacity-70'
        }`}></div>
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8">
        <div className={`transition-all duration-500 ${isHovered ? 'translate-y-0' : 'translate-y-2'}`}>
          <h3 className="font-sans text-lg sm:text-2xl font-bold text-white mb-1 sm:mb-2 tracking-wide leading-snug">
            {ensemble.name}
          </h3>
          <p className="font-sans text-xs sm:text-sm text-white/90 mb-2 sm:mb-4 leading-snug">
            {ensemble.description}
          </p>

          {/* Details - Show on Hover */}
          <div className={`transition-all duration-500 ${
            isHovered ? 'opacity-100 max-h-48' : 'opacity-0 max-h-0'
          } overflow-hidden`}>
            <p className="font-sans text-xs sm:text-sm text-white/80 leading-relaxed mb-3 sm:mb-4">
              {ensemble.details}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white/70">
                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                  <i className="ri-group-line text-base sm:text-lg"></i>
                </div>
                <span className="font-sans text-xs">{ensemble.members} members</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                  <i className="ri-play-circle-line text-base sm:text-lg"></i>
                </div>
                <span className="font-sans text-xs tracking-widest uppercase">Double click to watch</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
